import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, CheckCircle, ArrowLeft, Lightning, Info } from "@phosphor-icons/react";

const PRICE = 20; // MXN per change

const PACKS = [
  { qty: 1, label: "1 cambio", subtitle: "Para un retoque puntual" },
  { qty: 2, label: "2 cambios", subtitle: "Letra y audio, por ejemplo" },
  { qty: 5, label: "5 cambios", subtitle: "Para los perfeccionistas" },
];

export default function CambiosExtra() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [selectedQty, setSelectedQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);

  // After Stripe redirect with ?session_id=..., poll until paid (max 15 tries ≈ 60s).
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let tries = 0;
    const MAX_TRIES = 15;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refreshUser();
          toast.success("¡Cambios extra agregados a tu cuenta!");
          setPolling(false);
          nav("/cambios-extra", { replace: true });
          return;
        }
        if (tries < MAX_TRIES) {
          setTimeout(tick, 4000);
        } else {
          setPolling(false);
          toast.error("El pago tarda más de lo normal. Actualiza la página en un momento.");
        }
      } catch (e) {
        if (cancelled) return;
        console.warn("[CambiosExtra] poll failed:", e?.response?.status || e?.message);
        if (tries < MAX_TRIES) setTimeout(tick, 4000);
        else setPolling(false);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, nav, refreshUser]);

  const buy = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/regen-credits/checkout", {
        origin_url: window.location.origin,
        method: "stripe",
        quantity: selectedQty,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error iniciando el pago");
    } finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        <Link to="/library" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6" data-testid="cambios-back">
          <ArrowLeft size={16} /> Volver
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B4F34A]/10 border border-[#B4F34A]/20 mb-4">
          <ArrowsClockwise size={14} weight="bold" className="text-[#B4F34A]" />
          <span className="text-xs uppercase tracking-widest text-[#B4F34A] font-semibold">Tus cambios extra</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          Cambios extra
        </h1>
        <p className="text-white/60 mt-3 mb-8 max-w-xl leading-relaxed">
          Cada canción incluye <strong className="text-white">1 cambio gratis de letra</strong> y <strong className="text-white">1 cambio gratis de audio</strong>. Cuando quieras más cambios, cada uno cuesta <span className="text-[#FFB800] font-bold">$20 MXN</span>.
        </p>

        {/* Balance card */}
        <div className="rounded-2xl bg-gradient-to-br from-[#B4F34A]/10 to-[#FFB800]/5 border border-[#B4F34A]/20 p-6 mb-8" data-testid="balance-card">
          <p className="text-xs uppercase tracking-widest text-white/60 mb-2">Tu saldo actual</p>
          <div className="flex items-baseline gap-3">
            <span className="font-display text-5xl sm:text-6xl font-black text-[#B4F34A]" data-testid="balance-number">
              {user.regen_credits || 0}
            </span>
            <span className="text-white/60">cambio{user.regen_credits === 1 ? '' : 's'} disponible{user.regen_credits === 1 ? '' : 's'}</span>
          </div>
          <p className="text-xs text-white/40 mt-3">Cada cambio sirve para regenerar la letra, el audio o la portada de cualquier canción tuya.</p>
        </div>

        {polling && (
          <div className="rounded-2xl bg-[#FFB800]/10 border border-[#FFB800]/30 p-5 mb-6 flex items-center gap-3" data-testid="polling-banner">
            <div className="w-2 h-2 rounded-full bg-[#FFB800] animate-pulse shrink-0"></div>
            <p className="text-sm text-white/90">Confirmando tu pago… esto toma unos segundos.</p>
          </div>
        )}

        <h2 className="font-display text-2xl font-bold mb-4">Compra cambios</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="packs">
          {PACKS.map((p) => {
            const selected = selectedQty === p.qty;
            return (
              <button
                key={p.qty}
                onClick={() => setSelectedQty(p.qty)}
                data-testid={`pack-${p.qty}`}
                className={`relative text-left rounded-2xl p-5 border-2 transition-all ${
                  selected
                    ? 'bg-[#FFB800]/10 border-[#FFB800] scale-[1.02]'
                    : 'bg-[#121212] border-white/10 hover:border-white/30'
                }`}
              >
                <p className="font-display font-bold text-xl">{p.label}</p>
                <p className="text-xs text-white/50 mt-1">{p.subtitle}</p>
                <p className="font-display text-2xl font-black mt-3" style={{ color: selected ? '#FFB800' : '#fff' }}>
                  ${p.qty * PRICE} <span className="text-xs font-medium text-white/40">MXN</span>
                </p>
                {selected && (
                  <CheckCircle weight="fill" size={20} className="absolute top-3 right-3 text-[#FFB800]" />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-6">
          <Info size={18} className="text-[#FFB800] shrink-0 mt-0.5" />
          <p className="text-xs text-white/60 leading-relaxed">
            El precio es <strong className="text-white">lineal</strong>: $20 MXN por cambio, sin importar el tipo. No hay descuento por volumen — paga sólo lo que necesites.
          </p>
        </div>

        <button
          onClick={buy}
          disabled={loading || polling}
          className="w-full py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] disabled:opacity-50 text-black font-semibold transition-all active:scale-95 flex items-center justify-center gap-2"
          data-testid="buy-button"
        >
          <Lightning size={18} weight="fill" />
          {loading ? "Abriendo pago…" : `Comprar ${selectedQty} cambio${selectedQty === 1 ? '' : 's'} · $${selectedQty * PRICE} MXN`}
        </button>

        {/* How it works */}
        <div className="mt-12 space-y-4">
          <h3 className="font-display text-xl font-bold">¿Cómo funcionan?</h3>
          {[
            { t: "Letra", d: "Vuelve a generar la letra completa con la IA. Puedes ajustar el estilo, el tono o agregar instrucciones nuevas." },
            { t: "Audio", d: "Genera una versión musical diferente de la misma letra. Útil si la primera mezcla no te convenció." },
            { t: "Portada", d: "Sube una nueva foto y crea otra carátula con el filtro del género. (Esta no tiene cambio gratis incluido.)" },
          ].map((s) => (
            <div key={s.t} className="rounded-xl bg-[#0a0a0a] border border-white/5 p-4">
              <p className="font-display font-bold text-[#B4F34A]">{s.t}</p>
              <p className="text-sm text-white/60 mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
